import restaurantService from "@/services/restaurant";
import hasPlural from "@/components/report/shared/hasPlural";
import { DAYS, cycleRules, isBlank as isEmptyValue, okDays, okTime } from "./settingsLogic";
import {
  DEFAULT_SCREEN,
  GROUPS,
  GUEST_PICKUP_FIELDS,
  IMAGE_FIELDS,
  LINK_FIELDS,
  OPTION_SWITCHES,
  SCREENS,
  SEARCH_INDEX,
  clone,
  diffForms,
  fromApi,
  getPath,
  labelOfPath,
  normalizeApi,
  normalizeText,
  rankSearch,
  screenOfPath,
  setPath,
  toApi,
  validateForm,
} from "./settingsRegistry";
import { LEGACY_UI_VALUE, SETTINGS_QUERY } from "./settingsQuery";

const UNDO_MS = 12000;
const TOAST_MS = 4200;
const RELOAD_MS = 1400;
const todayIndex = () => (new Date().getDay() + 6) % 7;

/**
 * Zajedničko stanje stranice podešavanja restorana (računar i telefon): kopija "snimljeno / trenutno", razlike,
 * provjere, snimanje, vraćanje poslednjeg snimanja, upozorenje pri izlasku, pretraga i izbor ekrana.
 * Stranica ga drži (a ne ljuska), pa izmjene ne nestaju kad se prozor suzi do telefona i vrati.
 * Ljuske i dijelovi ga dobijaju preko `inject: { ctl: "settingsCtl" }`.
 */
export default {
  mixins: [hasPlural],
  provide() {
    return { settingsCtl: this };
  },
  data: () => ({
    ready: false,
    form: null,
    saved: null,
    base: null,
    previous: null,
    alphabet: "",
    galleryOpen: false,
    ui: {
      saving: false,
      tried: false,
      touched: {},
      banner: null,
      toast: null,
      pop: null,
      dlg: null,
      picker: null,
      query: "",
      bypass: false,
      sim: { days: { day: todayIndex(), side: "before" }, guest_day: { day: todayIndex(), side: "before" } },
      open: { days: todayIndex(), guest_day: todayIndex() },
      cycle: { h: 0, o: 3, time: "12:00" },
      uniform: { time: "12:00", before: 1, after: 2 },
      flash: "",
    },
  }),
  computed: {
    isPhone() {
      return Boolean(this.device && this.device.isMobile);
    },
    restaurant() {
      return this.$store.state.restaurant;
    },
    restaurantId() {
      return this.restaurant ? this.restaurant.id : null;
    },
    currency() {
      return (this.form && this.form.price) || "";
    },

    // ------------------------------------------------------------ ekrani
    groups() {
      return GROUPS.map((group) => ({
        key: group.key,
        title: this.$t(group.titleKey),
        items: SCREENS.filter((screen) => screen.group === group.key).map((screen) => ({
          key: screen.key,
          icon: screen.icon,
          title: this.$t(`settings_shell.screens.${screen.key}.title`),
          badge: this.dirtyIn(screen.key) || "",
          badgeOff: !this.dirtyIn(screen.key) && screen.group === "guests" && this.form && !this.form.lice_bez_ugovora,
        })),
      }));
    },
    screenKeys() {
      return SCREENS.map((screen) => screen.key);
    },
    currentKey() {
      const wanted = this.$route.query[SETTINGS_QUERY.screen];
      if (this.screenKeys.includes(wanted)) {
        return wanted;
      }
      return this.isPhone ? null : DEFAULT_SCREEN;
    },
    useLegacy() {
      return this.$route.query[SETTINGS_QUERY.legacy] === LEGACY_UI_VALUE && !this.isPhone;
    },

    // ------------------------------------------------------------ izmjene i provjere
    changes() {
      return this.form ? diffForms(this.saved, this.form) : [];
    },
    changeCount() {
      return this.changes.length;
    },
    dirtySet() {
      return new Set(this.changes.map((change) => change.path));
    },
    validation() {
      return this.form ? validateForm(this.form, (path) => this.dirtySet.has(path)) : { errors: {}, warnings: {} };
    },
    /** Greške koje se vide (polje je diran ili je pokušano snimanje). */
    visibleErrorPaths() {
      return Object.keys(this.validation.errors).filter((path) => this.ui.tried || this.ui.touched[path]);
    },
    errorCount() {
      return this.visibleErrorPaths.length;
    },
    changesByScreen() {
      const out = {};
      this.changes.forEach((change) => {
        const screen = screenOfPath(change.path);
        (out[screen] = out[screen] || []).push(change);
      });
      return out;
    },

    // ------------------------------------------------------------ zaglavlje ekrana
    kpis() {
      return this.currentKey ? this.kpisFor(this.currentKey) : [];
    },

    // ------------------------------------------------------------ pretraga
    searchEntries() {
      return SEARCH_INDEX.map((entry) => {
        const text = entry.literal || this.$t(entry.title);
        const own = normalizeText(`${text} ${entry.desc ? this.$t(entry.desc) : ""}`);
        const screenTitle = this.$t(`settings_shell.screens.${entry.screen}.title`);
        return { ...entry, text, screenTitle, own, all: `${own} ${normalizeText(screenTitle)}` };
      });
    },
    searchResults() {
      return rankSearch(this.searchEntries, this.ui.query);
    },
  },
  watch: {
    restaurantId() {
      this.init();
    },
    changeCount(count) {
      if (!count) {
        this.ui.pop = this.ui.pop === "changes" ? null : this.ui.pop;
      }
    },
  },
  created() {
    this.init();
  },
  mounted() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
    window.addEventListener("keydown", this.onSettingsKeydown);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    window.removeEventListener("keydown", this.onSettingsKeydown);
    clearTimeout(this.toastTimer);
    clearTimeout(this.flashTimer);
    clearTimeout(this.reloadTimer);
  },
  beforeRouteLeave(to, from, next) {
    if (!this.changeCount || this.ui.bypass || this.useLegacy) {
      next();
      return;
    }
    this.ui.dlg = { kind: "leave", to };
    next(false);
  },
  methods: {
    // ------------------------------------------------------------ učitavanje
    init() {
      const restaurant = this.restaurant;
      if (!restaurant) {
        this.ready = false;
        return;
      }
      this.base = normalizeApi(restaurant.settings, restaurant);
      this.saved = fromApi(this.base);
      this.form = clone(this.saved);
      this.previous = null;
      this.alphabet = restaurant.alphabet === "cir" || restaurant.alphabet === "lat" ? restaurant.alphabet : "";
      this.resetUi();
      this.ready = true;
    },
    resetUi() {
      Object.assign(this.ui, { saving: false, tried: false, touched: {}, banner: null, pop: null, dlg: null, picker: null, query: "" });
    },

    // ------------------------------------------------------------ čitanje i upis polja
    val(path) {
      return this.form ? getPath(this.form, path) : undefined;
    },
    set(path, value, touch = true) {
      setPath(this.form, path, value, this.$set);
      if (touch) {
        this.touch(path);
      }
    },
    toggle(path) {
      this.set(path, !this.val(path), false);
    },
    touch(path) {
      if (!this.ui.touched[path]) {
        this.$set(this.ui.touched, path, true);
      }
    },
    changed(path) {
      return this.dirtySet.has(path);
    },
    errorText(path) {
      const error = this.validation.errors[path];
      if (!error || !(this.ui.tried || this.ui.touched[path])) {
        return "";
      }
      return this.$t(`settings_shell.err.${error.code}`, error.params);
    },
    warnText(path) {
      const warning = this.validation.warnings[path];
      return warning ? this.$t(`settings_shell.err.${warning.code}`, warning.params) : "";
    },
    dirtyIn(screenKey) {
      return (this.changesByScreen[screenKey] || []).length;
    },
    revert(path) {
      setPath(this.form, path, clone(getPath(this.saved, path)), this.$set);
    },
    revertAll() {
      this.form = clone(this.saved);
      this.ui.pop = null;
    },
    discard() {
      if (!this.changeCount) {
        return;
      }
      this.form = clone(this.saved);
      Object.assign(this.ui, { touched: {}, tried: false, banner: null, pop: null });
      this.notify(this.$t("settings_shell.toast.discarded"));
    },
    imageUrl(name) {
      return name && this.restaurantId ? this.galleryUrl(name, "medium", this.restaurantId) : "";
    },

    // ------------------------------------------------------------ obavještenja
    notify(text, kind = "ok", action = null, label = "") {
      clearTimeout(this.toastTimer);
      this.ui.toast = { text, kind, label, action };
      this.toastTimer = setTimeout(() => {
        this.ui.toast = null;
      }, action ? UNDO_MS : TOAST_MS);
    },
    runToastAction() {
      const toast = this.ui.toast;
      this.ui.toast = null;
      if (toast && typeof toast.action === "function") {
        toast.action();
      }
    },
    dismissToast() {
      this.ui.toast = null;
    },

    // ------------------------------------------------------------ ekrani i adresa
    openScreen(key) {
      if (!this.screenKeys.includes(key)) {
        return Promise.resolve();
      }
      if (key === this.currentKey) {
        return Promise.resolve();
      }
      this.ui.pop = null;
      this.ui.query = "";
      return this.$router.push({ query: { ...this.$route.query, [SETTINGS_QUERY.screen]: key } }).catch(() => {});
    },
    closeScreen() {
      const query = { ...this.$route.query };
      delete query[SETTINGS_QUERY.screen];
      this.$router.replace({ query }).catch(() => {});
    },
    /** Otvara ekran i pokazuje stavku (rezultat pretrage): pomjera je u vidno polje i kratko je istakne. */
    goTo(screen, id) {
      return this.openScreen(screen).then(() => {
        this.ui.query = "";
        this.$nextTick(() => {
          const el = document.getElementById(`ss-${id}`);
          if (!el) {
            window.scrollTo(0, 0);
            return;
          }
          el.scrollIntoView({ block: "center" });
          this.ui.flash = id;
          clearTimeout(this.flashTimer);
          this.flashTimer = setTimeout(() => {
            this.ui.flash = "";
          }, 1800);
          const inner = el.querySelector("input, select, textarea, [role=switch], button");
          if (inner) {
            inner.focus({ preventScroll: true });
          }
        });
      });
    },
    openLegacy() {
      const go = () => this.$router.replace({ query: { ...this.$route.query, [SETTINGS_QUERY.legacy]: LEGACY_UI_VALUE } }).catch(() => {});
      if (this.changeCount) {
        this.ui.dlg = { kind: "leave", proceed: go };
        return;
      }
      go();
    },
    useNewView() {
      const query = { ...this.$route.query };
      delete query[SETTINGS_QUERY.legacy];
      this.$router.replace({ query }).catch(() => {});
    },

    // ------------------------------------------------------------ brza podešavanja rokova
    /** Sedmični ciklus: upisuje rokove za svih 7 dana. */
    applyCycle() {
      const { h, o, time } = this.ui.cycle;
      const rules = cycleRules(h, o, time || "12:00");
      DAYS.forEach((day, index) => {
        const row = this.form.days[day];
        row.day_before_limit = rules[index].before;
        row.day_after_limit = rules[index].after;
        row.time = time || "12:00";
      });
      this.ui.pop = null;
      this.notify(this.$t("settings_shell.toast.cycle_applied"));
    },
    applyUniform() {
      const { time, before, after } = this.ui.uniform;
      if (!okDays(before) || !okDays(after) || !okTime(time)) {
        this.notify(this.$t("settings_shell.toast.uniform_invalid"), "err");
        return;
      }
      DAYS.forEach((day) => {
        const row = this.form.days[day];
        row.day_before_limit = Number(before);
        row.day_after_limit = Number(after);
        row.time = time;
      });
      this.ui.pop = null;
      this.notify(this.$t("settings_shell.toast.uniform_applied"));
    },
    copyDay() {
      const index = this.ui.sim.days.day;
      const source = this.form.days[DAYS[index]];
      DAYS.forEach((day, i) => {
        if (i === index) {
          return;
        }
        const row = this.form.days[day];
        row.day_before_limit = source.day_before_limit;
        row.day_after_limit = source.day_after_limit;
        row.time = source.time;
      });
      this.ui.pop = null;
      this.notify(this.$t("settings_shell.toast.day_copied", { day: this.$t(DAYS[index]) }));
    },
    enableNewTimes() {
      this.ui.dlg = { kind: "legacy" };
    },
    confirmNewTimes() {
      this.set("new_time_changes", true, false);
      this.ui.dlg = null;
    },

    // ------------------------------------------------------------ slike
    openPicker(path) {
      this.ui.picker = path;
      this.galleryOpen = true;
    },
    pickImage(image) {
      this.galleryOpen = false;
      if (this.ui.picker && image) {
        this.set(this.ui.picker, image.name, false);
      }
      this.ui.picker = null;
    },
    askRemoveImage(path) {
      this.ui.dlg = { kind: "remove", path };
    },
    confirmRemoveImage() {
      const { path } = this.ui.dlg || {};
      if (path) {
        this.set(path, null, false);
      }
      this.ui.dlg = null;
    },

    // ------------------------------------------------------------ pismo (odmah, posebnim zahtjevom)
    async setAlphabet(value) {
      if (this.alphabet === value || !this.restaurantId) {
        return;
      }
      const previous = this.alphabet;
      this.alphabet = value;
      try {
        const res = await restaurantService.updateRestaurantTextLanguage(this.restaurantId, {
          lat: value === "lat",
          cir: value === "cir",
          hrv: "",
          alphabet: value,
        });
        this.$root.$emit("langChanged", res);
        this.$store.commit("SET_RESTAURANT_FIELDS", { id: this.restaurantId, fields: { alphabet: value } });
        this.notify(this.$t("settings_shell.toast.alphabet_saved"));
      } catch (error) {
        this.alphabet = previous;
        this.notify(this.$t("error_updating_alphabet"), "err");
      }
    },

    // ------------------------------------------------------------ snimanje
    firstErrorPath() {
      const paths = Object.keys(this.validation.errors);
      const order = SCREENS.map((screen) => screen.key);
      return paths.sort((a, b) => order.indexOf(screenOfPath(a)) - order.indexOf(screenOfPath(b)))[0] || null;
    },
    async focusFirstError() {
      const path = this.firstErrorPath();
      if (!path) {
        return;
      }
      const screen = screenOfPath(path);
      const match = path.match(/^(days|guest_day)\.(\w+)\./);
      if (match && this.isPhone) {
        const index = DAYS.indexOf(match[2]);
        this.ui.open[match[1]] = index;
        this.ui.sim[match[1]].day = index;
      }
      await this.openScreen(screen);
      this.$nextTick(() => {
        const el = document.querySelector(`[data-path="${path}"]`);
        if (el) {
          el.scrollIntoView({ block: "center" });
          const input = el.querySelector("input, select, textarea");
          if (input) {
            input.focus({ preventScroll: true });
          }
        }
      });
    },
    async save() {
      if (this.ui.saving || !this.changeCount) {
        return false;
      }
      this.ui.tried = true;
      this.ui.pop = null;
      const invalid = Object.keys(this.validation.errors).length;
      if (invalid) {
        this.focusFirstError();
        this.notify(this.$t("settings_shell.toast.fix_n", { n: invalid }), "err");
        return false;
      }
      const sent = clone(this.form);
      const payload = toApi(sent, this.saved, this.base);
      this.ui.saving = true;
      this.ui.banner = null;
      try {
        await restaurantService.updateRestaurantSettings(this.restaurantId, payload);
      } catch (error) {
        this.ui.saving = false;
        this.ui.banner = {
          title: this.$t("settings_shell.banner.save_failed"),
          text: this.serverMessage(error) || this.$t("settings_shell.banner.save_failed_text"),
        };
        return false;
      }
      this.afterSave(payload, sent);
      return true;
    },
    serverMessage(error) {
      const data = error && error.response && error.response.data;
      return data && typeof data.message === "string" && data.message.length < 200 ? data.message : "";
    },
    afterSave(payload, sent) {
      const languageChanged = JSON.stringify(this.base.language) !== JSON.stringify(payload.language);
      this.previous = { base: this.base, saved: this.saved };
      this.base = payload;
      this.saved = fromApi(payload);
      // ako je korisnik nastavio da mijenja dok je zahtjev trajao, njegove izmjene ostaju
      if (!diffForms(sent, this.form).length) {
        this.form = clone(this.saved);
      }
      Object.assign(this.ui, { saving: false, tried: false, touched: {} });
      this.syncStore(payload);
      if (languageChanged) {
        this.notify(this.$t("settings_shell.toast.saved_languages"));
        clearTimeout(this.reloadTimer);
        this.reloadTimer = setTimeout(() => window.location.reload(), RELOAD_MS);
        return;
      }
      this.notify(this.$t("settings_shell.toast.saved"), "ok", this.undo, this.$t("settings_shell.toast.undo"));
    },
    /** "Vrati": isti zahtjev sa podešavanjima prije zadnjeg snimanja. */
    async undo() {
      const previous = this.previous;
      if (!previous || this.ui.saving) {
        return;
      }
      this.ui.saving = true;
      try {
        await restaurantService.updateRestaurantSettings(this.restaurantId, previous.base);
      } catch (error) {
        this.ui.saving = false;
        this.notify(this.$t("settings_shell.toast.undo_failed"), "err");
        return;
      }
      this.base = previous.base;
      this.saved = fromApi(this.base);
      this.form = clone(this.saved);
      this.previous = null;
      this.ui.saving = false;
      this.syncStore(this.base);
      this.notify(this.$t("settings_shell.toast.undone"));
    },
    syncStore(settings) {
      this.$store.commit("SET_RESTAURANT_FIELDS", { id: this.restaurantId, fields: { settings } });
    },

    // ------------------------------------------------------------ izlazak sa nesnimljenim izmjenama
    leaveDiscard() {
      const dlg = this.ui.dlg || {};
      this.ui.dlg = null;
      this.ui.bypass = true;
      this.form = clone(this.saved);
      this.proceedAfterDialog(dlg);
    },
    async leaveSave() {
      const dlg = this.ui.dlg || {};
      this.ui.dlg = null;
      const ok = await this.save();
      if (ok) {
        this.ui.bypass = true;
        this.proceedAfterDialog(dlg);
      }
    },
    proceedAfterDialog(dlg) {
      if (typeof dlg.proceed === "function") {
        dlg.proceed();
      } else if (dlg.to) {
        this.$router.push(dlg.to).catch(() => {});
      }
      this.$nextTick(() => {
        this.ui.bypass = false;
      });
    },
    closeDialog() {
      this.ui.dlg = null;
    },
    onBeforeUnload(event) {
      if (this.changeCount && !this.useLegacy) {
        event.preventDefault();
        event.returnValue = "";
      }
    },
    onSettingsKeydown(event) {
      if (this.useLegacy || !this.ready) {
        return;
      }
      if ((event.ctrlKey || event.metaKey) && !event.altKey && String(event.key).toLowerCase() === "s") {
        event.preventDefault();
        this.save();
      }
    },

    // ------------------------------------------------------------ brojke za zaglavlje i početni ekran telefona
    countOn(paths) {
      return paths.filter((path) => this.val(path)).length;
    },
    nOf(n, total) {
      return this.$t("settings_shell.v.n_of", { n, total });
    },
    onOff(on) {
      return this.$t(on ? "settings_shell.v.on" : "settings_shell.v.off");
    },
    daysLabel(n) {
      return this.pluralT("settings_shell.v.days_n", Number(n) || 0);
    },
    minutesLabel(value) {
      return isEmptyValue(value) ? "—" : this.pluralT("settings_shell.v.minutes_n", Number(value) || 0);
    },
    kpisFor(key) {
      const c = this.form;
      if (!c) {
        return [];
      }
      const t = (name) => this.$t(`settings_shell.kpi.${name}`);
      const rows = DAYS.map((day) => c.days[day]);
      switch (key) {
        case "deadlines": {
          if (!c.new_time_changes) {
            return [{ label: t("deadline_mode"), value: this.$t("settings_shell.v.old_mode") }];
          }
          const times = [...new Set(rows.map((row) => row.time))];
          const farthest = Math.max(0, ...rows.map((row) => Math.max(Number(row.day_before_limit) || 0, Number(row.day_after_limit) || 0)));
          return [
            { label: t("deadline"), value: times.length === 1 ? times[0] || "—" : this.$t("settings_shell.v.by_day"), plain: times.length === 1 },
            { label: t("farthest"), value: this.daysLabel(farthest) },
            { label: t("no_limit"), value: this.onOff(c.no_time_limit) },
          ];
        }
        case "options":
          return [
            { label: t("enabled"), value: this.nOf(this.countOn(OPTION_SWITCHES), OPTION_SWITCHES.length) },
            { label: t("canteen"), value: isEmptyValue(c.timer) ? "—" : this.$t("settings_shell.v.seconds_short", { n: c.timer }) },
          ];
        case "offer":
          if (!c.lice_bez_ugovora) {
            return [{ label: t("guests"), value: this.onOff(false) }];
          }
          return [
            { label: t("guests"), value: this.onOff(true) },
            { label: t("pickup"), value: this.nOf(this.countOn(GUEST_PICKUP_FIELDS), GUEST_PICKUP_FIELDS.length) },
            { label: t("receiving"), value: this.onOff(c.receiving_orders), tone: c.receiving_orders ? "" : "warn" },
          ];
        case "delivery":
          if (!c.lice_bez_ugovora) {
            return [{ label: t("guests"), value: this.onOff(false) }];
          }
          return [
            { label: t("delivery"), value: `${c.delivery_from || "—"}–${c.delivery_to || "—"}`, plain: true },
            { label: t("cancel"), value: this.minutesLabel(c.guest.guest_cancel_order_time) },
            { label: t("ahead"), value: isEmptyValue(c.guest.dani_bez_menija) ? "—" : this.daysLabel(c.guest.dani_bez_menija) },
          ];
        case "appearance":
          return [{ label: t("images"), value: this.nOf(this.countOn(IMAGE_FIELDS), IMAGE_FIELDS.length) }];
        case "language":
          return [
            { label: t("languages"), value: String(c.language.length), plain: true },
            { label: t("alphabet"), value: this.$t(this.alphabet === "cir" ? "settings_shell.v.cyrillic" : "settings_shell.v.latin") },
            { label: t("currency"), value: c.price || "—", plain: true },
          ];
        case "links":
          return [{ label: t("filled"), value: this.nOf(this.countOn(LINK_FIELDS), LINK_FIELDS.length) }];
        default:
          return [];
      }
    },
    /** Jedan red sažetka za početni ekran telefona. */
    summaryFor(key) {
      return this.kpisFor(key)
        .slice(0, 2)
        .map((kpi) => `${kpi.label} ${kpi.value}`)
        .join(" · ");
    },
    screenTitle(key) {
      return this.$t(`settings_shell.screens.${key}.title`);
    },
    screenSub(key) {
      return this.$t(`settings_shell.screens.${key}.sub`);
    },
    labelFor(path) {
      const { key, day } = labelOfPath(path);
      return key ? this.$t(key, { day: day ? this.$t(day) : "" }) : path;
    },
    formatValue(path, value) {
      if (value === true) {
        return this.$t("settings_shell.v.on");
      }
      if (value === false) {
        return this.$t("settings_shell.v.off");
      }
      if (Array.isArray(value)) {
        return value.length ? value.join(", ") : "—";
      }
      return isEmptyValue(value) ? "—" : String(value);
    },
  },
};
